/**
 * Tracking Publicitario — Backoffice (solo SuperAdmin).
 *
 * Permite configurar por LICENCIA los IDs y tokens de tracking externo:
 *  - Meta Conversions API (CAPI) — pixel + access_token cifrado
 *  - Facebook Pixel — pixel ID
 *  - Google Tag Manager — container ID
 *  - LinkedIn Insight Tag — partner ID
 *  - Snippets personalizados (chatbots, otros medidores, widgets) en
 *    head / body_start / body_end
 *
 * La configuración se guarda por tipo de licencia (agency, developer,
 * education, health, etc.). Cada empresa de esa licencia recibirá la misma
 * configuración cuando llegue el momento de inyectarla en sus landings.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Activity, Code, Eye, EyeOff, Facebook, Linkedin, Loader2, PlusCircle, Save, Tag, Trash2 } from 'lucide-react';

const LICENSE_LABELS = {
  agency: 'Inmobiliario (Agencia)',
  independent: 'Independiente',
  developer: 'Desarrollador',
  franchise: 'Franquicia',
  owner: 'Owner',
  car_dealer: 'Automotriz',
  education: 'Educación',
  retail: 'Retail',
  generic: 'Genérico',
};
const LICENSE_ORDER = ['agency', 'independent', 'developer', 'franchise', 'owner', 'car_dealer', 'education', 'retail', 'generic'];
const POSITION_LABELS = { head: '<head>', body_start: '<body> inicio', body_end: '<body> fin' };

function emptyConfig(license_type) {
  return {
    license_type,
    meta_capi: { pixel_id: '', access_token_preview: '', has_access_token: false, enabled: false },
    fb_pixel: { pixel_id: '', enabled: false },
    gtm: { container_id: '', enabled: false },
    linkedin_insight: { partner_id: '', enabled: false },
    custom_snippets: [],
    updated_at: null,
    updated_by: null,
  };
}

function ProviderCard({ icon: Icon, title, badge, children, accent = 'cyan' }) {
  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid={`provider-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg bg-${accent}-400/10 flex items-center justify-center`}>
            <Icon className={`w-4 h-4 text-${accent}-300`} />
          </div>
          <h4 className="text-sm font-heading font-semibold text-white">{title}</h4>
          {badge && <span className="ml-auto text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-muted-foreground">{badge}</span>}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function LicenseEditor({ licenseType }) {
  const [config, setConfig] = useState(emptyConfig(licenseType));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCapiToken, setShowCapiToken] = useState(false);
  const [capiTokenInput, setCapiTokenInput] = useState(''); // separado: si vacío, NO se envía
  const [capiTokenTouched, setCapiTokenTouched] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ad-tracking/${licenseType}`);
      setConfig({ ...emptyConfig(licenseType), ...r.data });
    } catch (e) {
      toast.error('No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCapiTokenInput('');
    setCapiTokenTouched(false);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseType]);

  const setField = (path, value) => {
    setConfig(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const addSnippet = () => {
    setConfig(prev => ({
      ...prev,
      custom_snippets: [...(prev.custom_snippets || []), {
        id: `temp-${Date.now()}`,
        label: 'Nuevo snippet',
        code: '',
        position: 'head',
        enabled: true,
        notes: '',
      }],
    }));
  };

  const updateSnippet = (id, patch) => {
    setConfig(prev => ({
      ...prev,
      custom_snippets: (prev.custom_snippets || []).map(s => s.id === id ? { ...s, ...patch } : s),
    }));
  };

  const removeSnippet = (id) => {
    setConfig(prev => ({
      ...prev,
      custom_snippets: (prev.custom_snippets || []).filter(s => s.id !== id),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        meta_capi: {
          pixel_id: config.meta_capi?.pixel_id || '',
          enabled: !!config.meta_capi?.enabled,
        },
        fb_pixel: {
          pixel_id: config.fb_pixel?.pixel_id || '',
          enabled: !!config.fb_pixel?.enabled,
        },
        gtm: {
          container_id: config.gtm?.container_id || '',
          enabled: !!config.gtm?.enabled,
        },
        linkedin_insight: {
          partner_id: config.linkedin_insight?.partner_id || '',
          enabled: !!config.linkedin_insight?.enabled,
        },
        custom_snippets: (config.custom_snippets || []).map(s => ({
          id: s.id?.startsWith('temp-') ? undefined : s.id,
          label: s.label,
          code: s.code,
          position: s.position,
          enabled: s.enabled,
          notes: s.notes,
        })),
      };
      // Solo enviamos access_token si fue tocado (preservamos cifrado existente si no)
      if (capiTokenTouched) {
        body.meta_capi.access_token = capiTokenInput;
      }
      const r = await api.put(`/admin/ad-tracking/${licenseType}`, body);
      setConfig({ ...emptyConfig(licenseType), ...r.data });
      setCapiTokenInput('');
      setCapiTokenTouched(false);
      toast.success(`Configuración guardada para ${LICENSE_LABELS[licenseType] || licenseType}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al guardar';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="ad-tracking-loading">
        <Loader2 className="w-6 h-6 text-cyan-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid={`ad-tracking-editor-${licenseType}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Configuración para licencia</p>
          <h3 className="font-heading text-lg font-semibold text-white">{LICENSE_LABELS[licenseType] || licenseType}</h3>
          {config.updated_at && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Última actualización: {new Date(config.updated_at).toLocaleString('es-MX')} · {config.updated_by || '—'}
            </p>
          )}
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs h-9" data-testid="ad-tracking-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Guardar cambios
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Meta CAPI */}
        <ProviderCard icon={Activity} title="Meta Conversions API" badge="CAPI" accent="cyan">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Activar integración</Label>
            <Switch checked={!!config.meta_capi?.enabled} onCheckedChange={v => setField('meta_capi.enabled', v)} data-testid="capi-enabled-toggle" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pixel ID</Label>
            <Input
              value={config.meta_capi?.pixel_id || ''}
              onChange={e => setField('meta_capi.pixel_id', e.target.value)}
              placeholder="ej. 1234567890123456"
              className="bg-black/20 border-white/10 text-xs h-9"
              data-testid="capi-pixel-id"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Access Token</Label>
              {config.meta_capi?.has_access_token && !capiTokenTouched && (
                <span className="text-[10px] text-emerald-400">Guardado: {config.meta_capi.access_token_preview}</span>
              )}
            </div>
            <div className="relative">
              <Input
                type={showCapiToken ? 'text' : 'password'}
                value={capiTokenInput}
                onChange={e => { setCapiTokenInput(e.target.value); setCapiTokenTouched(true); }}
                placeholder={config.meta_capi?.has_access_token ? '••••••••••• (dejar vacío conserva el actual)' : 'Pega tu access token aquí'}
                className="bg-black/20 border-white/10 text-xs h-9 pr-8"
                data-testid="capi-token-input"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowCapiToken(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                data-testid="capi-token-toggle-visibility"
              >
                {showCapiToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground">El token se guarda cifrado. Solo verás los últimos 4 caracteres después de guardar.</p>
          </div>
        </ProviderCard>

        {/* Facebook Pixel */}
        <ProviderCard icon={Facebook} title="Facebook Pixel" badge="Pixel ID" accent="violet">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Activar integración</Label>
            <Switch checked={!!config.fb_pixel?.enabled} onCheckedChange={v => setField('fb_pixel.enabled', v)} data-testid="fb-pixel-enabled-toggle" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pixel ID</Label>
            <Input
              value={config.fb_pixel?.pixel_id || ''}
              onChange={e => setField('fb_pixel.pixel_id', e.target.value)}
              placeholder="ej. 1234567890123456"
              className="bg-black/20 border-white/10 text-xs h-9"
              data-testid="fb-pixel-id"
            />
          </div>
        </ProviderCard>

        {/* Google Tag Manager */}
        <ProviderCard icon={Tag} title="Google Tag Manager" badge="GTM" accent="amber">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Activar integración</Label>
            <Switch checked={!!config.gtm?.enabled} onCheckedChange={v => setField('gtm.enabled', v)} data-testid="gtm-enabled-toggle" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Container ID</Label>
            <Input
              value={config.gtm?.container_id || ''}
              onChange={e => setField('gtm.container_id', e.target.value)}
              placeholder="ej. GTM-XXXXXXX"
              className="bg-black/20 border-white/10 text-xs h-9"
              data-testid="gtm-container-id"
            />
          </div>
        </ProviderCard>

        {/* LinkedIn Insight */}
        <ProviderCard icon={Linkedin} title="LinkedIn Insight Tag" badge="B2B" accent="sky">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Activar integración</Label>
            <Switch checked={!!config.linkedin_insight?.enabled} onCheckedChange={v => setField('linkedin_insight.enabled', v)} data-testid="linkedin-enabled-toggle" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Partner ID</Label>
            <Input
              value={config.linkedin_insight?.partner_id || ''}
              onChange={e => setField('linkedin_insight.partner_id', e.target.value)}
              placeholder="ej. 1234567"
              className="bg-black/20 border-white/10 text-xs h-9"
              data-testid="linkedin-partner-id"
            />
          </div>
        </ProviderCard>
      </div>

      {/* Custom snippets */}
      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="custom-snippets-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 flex items-center justify-center">
                <Code className="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <h4 className="text-sm font-heading font-semibold text-white">Snippets personalizados</h4>
                <p className="text-[11px] text-muted-foreground">Inserta cualquier script o widget (chatbots, otros medidores, encuestas) por ubicación.</p>
              </div>
            </div>
            <Button onClick={addSnippet} variant="outline" size="sm" className="border-white/10 text-xs h-8" data-testid="add-snippet-btn">
              <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> Agregar snippet
            </Button>
          </div>

          {(config.custom_snippets || []).length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">No hay snippets configurados aún.</p>
          )}

          <div className="space-y-3">
            {(config.custom_snippets || []).map((s, idx) => (
              <div key={s.id} className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-2" data-testid={`snippet-${idx}`}>
                <div className="grid md:grid-cols-[1fr_180px_auto_auto] gap-2 items-end">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Etiqueta</Label>
                    <Input
                      value={s.label}
                      onChange={e => updateSnippet(s.id, { label: e.target.value })}
                      placeholder="ej. Tawk.to chat"
                      className="bg-black/20 border-white/10 text-xs h-8"
                      data-testid={`snippet-${idx}-label`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ubicación</Label>
                    <Select value={s.position} onValueChange={v => updateSnippet(s.id, { position: v })}>
                      <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid={`snippet-${idx}-position`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(POSITION_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!s.enabled} onCheckedChange={v => updateSnippet(s.id, { enabled: v })} data-testid={`snippet-${idx}-enabled`} />
                    <span className="text-[10px] text-muted-foreground">{s.enabled ? 'On' : 'Off'}</span>
                  </div>
                  <Button onClick={() => removeSnippet(s.id)} variant="ghost" size="sm" className="text-rose-400 hover:text-rose-300 hover:bg-rose-400/10 h-8 px-2" data-testid={`snippet-${idx}-remove`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Código HTML/JS</Label>
                  <Textarea
                    value={s.code}
                    onChange={e => updateSnippet(s.id, { code: e.target.value })}
                    placeholder='<script>/* tu código aquí */</script>'
                    rows={4}
                    className="bg-black/30 border-white/10 text-[11px] font-mono"
                    data-testid={`snippet-${idx}-code`}
                  />
                </div>
                <Input
                  value={s.notes || ''}
                  onChange={e => updateSnippet(s.id, { notes: e.target.value })}
                  placeholder="Notas internas (opcional)"
                  className="bg-black/20 border-white/10 text-[11px] h-7"
                  data-testid={`snippet-${idx}-notes`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdTracking() {
  const [activeLicense, setActiveLicense] = useState('agency');

  return (
    <div data-testid="ad-tracking-page">
      <div className="mb-6">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Tracking Publicitario</h2>
        <p className="text-muted-foreground text-xs sm:text-sm mt-1">
          Pixels y CAPI de la <b className="text-white/80">landing corporativa LeadCEM</b>, por vertical.
          El pixel del tenant vive en Configuración → CAPI, no aquí.
        </p>
      </div>

      <Tabs value={activeLicense} onValueChange={setActiveLicense} className="w-full">
        <TabsList className="bg-[#1F2833]/80 border border-white/5 mb-5 flex flex-wrap h-auto" data-testid="ad-tracking-license-tabs">
          {LICENSE_ORDER.map(lt => (
            <TabsTrigger
              key={lt}
              value={lt}
              className="data-[state=active]:bg-cyan-400/15 data-[state=active]:text-cyan-400 text-xs"
              data-testid={`license-tab-${lt}`}
            >
              {LICENSE_LABELS[lt] || lt}
            </TabsTrigger>
          ))}
        </TabsList>

        {LICENSE_ORDER.map(lt => (
          <TabsContent key={lt} value={lt}>
            {activeLicense === lt && <LicenseEditor licenseType={lt} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
